import MetricCard from "@/components/cards/metric-card";
import WidgetCard from "@/components/cards/widget-card";
import DropdownAction from "@/components/charts/dropdown-action";
import ContainersIcon from "@/components/icons/containers";
import ExpenseIcon from "@/components/icons/expenses";
import ExpressDeliveryIcon from "@/components/icons/express-delivery";
import RevenueUpIcon from "@/components/icons/revenue-up";
import SalesIcon from "@/components/icons/sales";
import ShippingBox from "@/components/icons/shipping-box";
import { Order, OrderStatus } from "@/types";
import SimpleBar from "@/ui/simplebar";
import cn from "@/utils/class-names";
import { useEffect } from "react";
import { PiShippingContainer } from "react-icons/pi";
import { Text } from "rizzui";

export default function StatCards({
    className,
    orders,
}: {
    className?: string;
    orders: Order[];
}) {
    const statData = [
        {
            id: "1",
            title: "Orders",
            icon: <ExpenseIcon className="h-7 w-7" />,
            graphIcon: <PiShippingContainer className="me-1 h-4 w-4" />,
            graphColor: "text-red",
            metric: orders?.length ?? 0,
            increased: true,
            percentage:
                (((orders?.length ?? 0) / orders?.length) * 100).toFixed(2) ??
                0,
        },
        {
            id: "2",
            title: "Requested Orders",
            icon: <RevenueUpIcon className="h-7 w-7" />,
            graphIcon: <PiShippingContainer className="me-1 h-4 w-4" />,
            graphColor: "text-green",
            metric:
                orders?.filter(
                    (order) => order.status === OrderStatus.Requested,
                ).length ?? 0,
            increased: true,
            percentage:
                orders && orders.length > 0
                    ? (
                          ((orders?.filter(
                              (order) => order.status === OrderStatus.Requested,
                          ).length ?? 0) /
                              orders?.length) *
                          100
                      ).toFixed(2)
                    : "0",
        },
        {
            id: "3",
            title: "Processing Orders",
            icon: <SalesIcon className="h-9 w-9" />,
            graphIcon: <PiShippingContainer className="me-1 h-4 w-4" />,
            graphColor: "text-green",
            metric:
                orders?.filter(
                    (order) => order.status === OrderStatus.Processing,
                ).length ?? 0,
            increased: true,
            percentage:
                orders && orders.length > 0
                    ? ((
                          ((orders?.filter(
                              (order) =>
                                  order.status === OrderStatus.Processing,
                          ).length ?? 0) /
                              orders?.length) *
                          100
                      ).toFixed(2) ?? 0)
                    : "0",
        },
        {
            id: "4",
            title: "Purchased Orders ",
            icon: <ContainersIcon className="h-7 w-7" />,
            graphIcon: <PiShippingContainer className="me-1 h-4 w-4" />,
            graphColor: "text-green",
            metric:
                orders?.filter(
                    (order) => order.status === OrderStatus.Purchased,
                ).length ?? 0,
            increased: true,
            percentage:
                orders && orders.length > 0
                    ? ((
                          ((orders?.filter(
                              (order) => order.status === OrderStatus.Purchased,
                          ).length ?? 0) /
                              orders?.length) *
                          100
                      ).toFixed(2) ?? 0)
                    : "0",
        },
        {
            id: "5",
            title: "InTransit Orders",
            icon: <ExpressDeliveryIcon className="h-7 w-7" />,
            graphIcon: <PiShippingContainer className="me-1 h-4 w-4" />,
            graphColor: "text-red",
            metric:
                orders?.filter(
                    (order) => order.status === OrderStatus.InTransit,
                ).length ?? 0,
            decreased: true,
            percentage:
                orders && orders.length > 0
                    ? ((
                          ((orders?.filter(
                              (order) => order.status === OrderStatus.InTransit,
                          ).length ?? 0) /
                              orders?.length) *
                          100
                      ).toFixed(2) ?? 0)
                    : "0",
        },
        {
            id: "6",
            title: "Delivered Orders",
            icon: <ShippingBox className="h-7 w-7" />,
            graphIcon: <PiShippingContainer className="me-1 h-4 w-4" />,
            graphColor: "text-red",
            metric:
                orders?.filter(
                    (order) => order.status === OrderStatus.Delivered,
                ).length ?? 0,
            decreased: true,
            percentage:
                orders && orders.length > 0
                    ? ((
                          ((orders?.filter(
                              (order) => order.status === OrderStatus.Delivered,
                          ).length ?? 0) /
                              orders?.length) *
                          100
                      ).toFixed(2) ?? 0)
                    : "0",
        },
    ];

    const viewOptions = [
        {
            value: "today",
            label: "Today",
        },
        {
            value: "this-week",
            label: "This Week",
        },
    ];

    function handleChange(viewType: string) {
        console.log("viewType", viewType);
    }

    return (
        <WidgetCard
            rounded="lg"
            className={className}
            title="General Overview"
            headerClassName="mb-2 @2xl:mb-5"
            action={
                <DropdownAction
                    options={viewOptions}
                    onChange={handleChange}
                    dropdownClassName="!z-0"
                />
            }
        >
            <SimpleBar>
                <div className="grid grid-flow-col gap-5 pb-1">
                    {statData.map((stat, index) => (
                        <MetricCard
                            key={stat.title + stat.id}
                            title={stat.title}
                            metric={stat.metric}
                            icon={stat.icon}
                            className="min-w-[240px] border-0 p-1 @2xl:min-w-[280px] lg:p-1"
                            titleClassName="capitalize"
                            contentClassName="ps-5"
                            iconClassName={cn("@5xl:w-20 @5xl:h-20 h-16 w-16")}
                            chartClassName="hidden @[200px]:flex @[200px]:items-center h-14 w-24"
                        >
                            {index !== 0 ? (
                                <Text className="mt-5 flex items-center leading-none text-gray-500">
                                    <Text
                                        as="span"
                                        className={
                                            "me-2 inline-flex items-center font-medium text-green"
                                        }
                                    >
                                        {stat.graphIcon}
                                        {stat.percentage}%
                                    </Text>
                                    <Text
                                        as="span"
                                        className="me-1 hidden @[240px]:inline-flex"
                                    >
                                        of all orders
                                    </Text>
                                </Text>
                            ) : (
                                <Text className="mt-5 flex items-center leading-none text-gray-500">
                                    All Orders
                                </Text>
                            )}
                        </MetricCard>
                    ))}
                </div>
            </SimpleBar>
        </WidgetCard>
    );
}
